import React, { useEffect, useRef, useState } from 'react';
import { Platform, StatusBar, View } from 'react-native';
import { Stack } from 'expo-router';
import { QueryClientProvider } from '@tanstack/react-query';
import Toast from 'react-native-toast-message';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { PortalHost } from '@rn-primitives/portal';
import { FullWindowOverlay } from 'react-native-screens';
import { useFonts, Oswald_600SemiBold } from '@expo-google-fonts/oswald';
import { Archivo_900Black } from '@expo-google-fonts/archivo';
import {
  SpaceGrotesk_400Regular,
  SpaceGrotesk_600SemiBold,
} from '@expo-google-fonts/space-grotesk';
import { IBMPlexMono_600SemiBold } from '@expo-google-fonts/ibm-plex-mono';
import { queryClient } from '@/lib/queryClient';
import { TRPCProvider, trpcClient } from '@/lib/trpc';
import { setupQueryPersistence } from '@/lib/queryPersistence';
import { initializeBackgroundRefresh } from '@/lib/backgroundRefresh';
import { useAppDataRefresh } from '@/hooks/useAppDataRefresh';
import { useAuth } from '@/hooks/useAuth';
import { colors } from '@/lib/theme';
import { config } from '@/config/env';
import { DatadogWrapper } from '@/components/DatadogWrapper';
import { DatadogRouteTracker } from '@/components/DatadogRouteTracker';
import WebFeedbackWidget from '@/components/WebFeedbackWidget';
import { BootErrorBoundary } from '@/components/BootErrorBoundary';
import { AuthBootController, PushNotificationHandler } from '@/components/AppBoot';
import { LiveActivitySync } from '@/components/LiveActivitySync';
import { maybeNativeRumConfig, trackRumAction } from '@/lib/datadog';
import { StyleSheet } from 'react-native-unistyles';

const styles = StyleSheet.create(() => ({
  flex: {
    flex: 1,
  },
}));

function AppContent({ 
  authBootTimedOut, 
  setAuthBootTimedOut, 
  isRouterReady 
}: { 
  authBootTimedOut: boolean;
  setAuthBootTimedOut: (val: boolean) => void;
  isRouterReady: boolean;
}) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  useEffect(() => {
    if (isRouterReady && !authLoading) {
      trackRumAction('router_ready', { authBootTimedOut, isAuthenticated });
    }
  }, [authBootTimedOut, isRouterReady, authLoading, isAuthenticated]);

  return (
    <>
      <AuthBootController timedOut={authBootTimedOut} onTimeoutChange={setAuthBootTimedOut} />
      <PushNotificationHandler isReady={isRouterReady} />
      <LiveActivitySync isReady={isRouterReady} />
      <Stack screenOptions={{ headerShown: false }}>
        <Stack.Screen name="index" />
        <Stack.Screen name="(public)" />
        <Stack.Screen name="(app)" />
      </Stack>
      <DatadogRouteTracker />
    </>
  );
}

export default function RootLayout() {
  useAppDataRefresh();
  useFonts({
    Archivo_900Black,
    SpaceGrotesk_400Regular,
    SpaceGrotesk_600SemiBold,
    IBMPlexMono_600SemiBold,
    Oswald_600SemiBold,
  });

  const bootStageRef = useRef('app_start');
  const [authBootTimedOut, setAuthBootTimedOut] = useState(false);
  const [isRouterReady, setIsRouterReady] = useState(false);
  const diagnosticsEnabled = config.bootDiagnosticsEnabled;
  const datadogConfig = maybeNativeRumConfig();
  const WindowOverlay = Platform.OS === 'ios' ? FullWindowOverlay : React.Fragment;

  useEffect(() => {
    bootStageRef.current = 'app_mounted';
    trackRumAction('app_mounted');
    const timeoutId = setTimeout(() => {
      trackRumAction('boot_timeout', { stage: bootStageRef.current });
    }, 8000);
    return () => clearTimeout(timeoutId);
  }, []);

  useEffect(() => {
    setupQueryPersistence();
    void initializeBackgroundRefresh();
  }, []);

  useEffect(() => {
    if (!isRouterReady) {
      setIsRouterReady(true);
      bootStageRef.current = 'router_ready';
    }
  }, [isRouterReady]);

  return (
    <DatadogWrapper configuration={datadogConfig}>
      <BootErrorBoundary diagnosticsEnabled={diagnosticsEnabled}>
        <View style={styles.flex}>
          <SafeAreaProvider>
            <QueryClientProvider client={queryClient}>
              <TRPCProvider trpcClient={trpcClient} queryClient={queryClient}>
                <AppContent 
                  authBootTimedOut={authBootTimedOut}
                  setAuthBootTimedOut={setAuthBootTimedOut}
                  isRouterReady={isRouterReady}
                />
                <StatusBar barStyle="dark-content" backgroundColor={colors.background} />
                {Platform.OS === 'web' ? null : <WebFeedbackWidget />}
                <Toast />
                <WindowOverlay>
                  <PortalHost />
                </WindowOverlay>
              </TRPCProvider>
            </QueryClientProvider>
          </SafeAreaProvider>
        </View>
      </BootErrorBoundary>
    </DatadogWrapper>
  );
}
